import { setMany } from 'idb-keyval';
import { extInfo } from 'lib/extInfo';
import { HttpErrorCodes } from 'reactApp/api/HttpErrorCodes';
import { IS_OWN_PUBLIC } from 'reactApp/appHelpers/configHelpers';
import { ALL_DOCUMENTS_FOLDER_ID, ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { ALBUMS_FOLDER_NAME } from 'reactApp/modules/albums/albums.constants';
import { getNameById } from 'reactApp/modules/file/utils';
import { PERSONAL_DOCUMENTS_FOLDER_NAME } from 'reactApp/modules/personalDocuments/personalDocuments.constants';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { ConnectionFail } from 'reactApp/modules/uploading/fails/ConnectionFail';
import { InvalidCharactesFail } from 'reactApp/modules/uploading/fails/InvalidCharactesFail';
import { NameTooLongFail } from 'reactApp/modules/uploading/fails/NameTooLongFail';
import { PathTooLongFail } from 'reactApp/modules/uploading/fails/PathTooLongFail';
import { getVisibleNameParts, getVisiblePath, joinPath } from 'reactApp/modules/uploading/helpers/fs/fs.helpers';
import { UploadingDescriptor } from 'reactApp/modules/uploading/serviceClasses/UploadingDescriptor';
import { UploadingReason } from 'reactApp/modules/uploading/serviceClasses/UploadingReason';
import { EUploadingState, IDescriptorOptions } from 'reactApp/modules/uploading/uploading.types';
import { EFileError, EFileStatus, IInputFile } from 'reactApp/modules/uploadList/uploadList.model';
import { UrlBuilder } from 'reactApp/modules/urlBuilder/UrlBuilder';
import { sendXray } from 'reactApp/utils/ga';
import { getFileExtLimit } from 'reactApp/utils/textHelpers';

import { removeInvalidNameCharacters, removeInvalidPathCharacters, validatePaths } from './cloudFs/cloudFs.helpers';

const urlBuilder = new UrlBuilder();

const MAX_FILES_TO_STORE_IN_INDEX_DB = 1000;

const retryableHttpStatuses = [
    HttpErrorCodes.REQUEST_TIMEOUT,
    HttpErrorCodes.INTERNAL_SERVER_ERROR,
    HttpErrorCodes.BAD_GATEWAY,
    HttpErrorCodes.SERVICE_UNAVAILABLE,
    HttpErrorCodes.GATEWAY_TIMEOUT,
];

export const isRetryableHttpStatus = (status: number) => retryableHttpStatuses.includes(status);

export const sendGaUploaderNew = (action: string, label = '', iData: object | null | number = null) =>
    sendXray(['uploader', action, label], typeof iData === 'number' ? { i: iData } : iData);

export function filterDescriptors(descriptors: UploadingDescriptor[], isFolderAllowed: boolean) {
    return descriptors.filter((descriptor) => {
        const isDirectory = descriptor.isDirectory;
        const hasNotChildren = !isDirectory || !descriptor.children.length;

        if (isDirectory && !isFolderAllowed) {
            return false;
        }

        // Оставляем только пустые папки и файлы.
        // Остальные папки будут автоматически
        // созданы нашим API при добавлении файлов.
        return (
            hasNotChildren &&
            !(
                (descriptor.uploadingPacketConfig.storage === EStorageType.public ||
                    descriptor.uploadingPacketConfig.storage === EStorageType.stock) &&
                isDirectory
            )
        );
    });
}

export function getWorkingDirectory(
    storage: EStorageType,
    currentFolder,
    originalCloudPath: string | null = null,
    isSpecificDocument = false
) {
    // при продолжении загрузки используем изначальную директорию
    if (originalCloudPath) {
        return originalCloudPath;
    }
    const { isDocuments, isHome, isPublic, isReactPage, isAllDocuments } = getStorage(storage);

    if (isDocuments && isSpecificDocument) {
        return `/${PERSONAL_DOCUMENTS_FOLDER_NAME}`;
    }

    if (storage === EStorageType.albums) {
        return `/${ALBUMS_FOLDER_NAME}`;
    }

    if (isAllDocuments) {
        return `/${ALL_DOCUMENTS_FOLDER_ID}`;
    }

    if (isReactPage) {
        if (isHome) {
            return currentFolder.id;
        }

        return ROOT_FOLDER_ID;
    }

    if (isPublic && currentFolder?.home) {
        return currentFolder.home;
    }

    return currentFolder && currentFolder.id;
}

export function prepareDescriptors(descriptors: UploadingDescriptor[], workingDirectory: string): void {
    for (const descriptor of descriptors) {
        const localName = descriptor.localName;
        let localPath = descriptor.localPath;
        let cloudPath = joinPath(workingDirectory, localPath);
        let cloudName = getNameById(cloudPath);
        let error = descriptor.error;

        error = validatePaths({ error, path: localName });
        error = validatePaths({ error, path: cloudName });
        error = validatePaths({ error, path: cloudPath, onlyPath: true });

        const { fixError, fixCloudName, fixLocalPath, fixCloudPath, hasInvalidCharAutoFix, hasNameTooLongAutoFix } = autoFixError({
            error,
            cloudName,
            cloudPath,
            localPath,
        });
        error = fixError;
        cloudName = fixCloudName;
        cloudPath = fixCloudPath;
        localPath = fixLocalPath;

        if (error) {
            descriptor.error = error;
            if (!(error instanceof ConnectionFail)) {
                descriptor.state = EUploadingState.STATE_FAIL;
            }
        }

        descriptor.hasInvalidCharAutoFix = hasInvalidCharAutoFix;
        descriptor.hasNameTooLongAutoFix = hasNameTooLongAutoFix;

        descriptor.cloudName = cloudName;
        descriptor.cloudPath = cloudPath;
        descriptor.initialCloudPath = cloudPath;
        descriptor.localPath = localPath;
        descriptor.visiblePath = getVisiblePath(cloudPath);
        descriptor.visibleName = getVisiblePath(cloudName);
        descriptor.nameParts = getVisibleNameParts(cloudName);
        descriptor.uploadingPacketConfig.isPublicUpload = descriptor.uploadingPacketConfig.storage === EStorageType.public;
        descriptor.uploadingPacketConfig.isOwnPublic = IS_OWN_PUBLIC;
        descriptor.uploadingPacketConfig.workingDirectory = workingDirectory;
    }
}

// eslint-disable-next-line complexity,sonarjs/cognitive-complexity
export async function prepareFileDescriptorsForUI(descriptors: UploadingDescriptor[], descriptorOptions: IDescriptorOptions) {
    const fileDescriptors: IInputFile[] = [];

    const entries: [string, any][] = [];

    for (const descriptor of descriptors) {
        const { cloudPath, initialCloudPath, size, nameParts, id, localPath, error, isDirectory, hasInvalidCharAutoFix, hasNameTooLongAutoFix } = descriptor;
        const directoryFolder = isDirectory ? 'folder' : '';
        const extension = nameParts?.extension || directoryFolder;

        if (isDirectory && descriptor.cloudPath) {
            continue;
        }

        switch (true) {
            case error instanceof NameTooLongFail:
            case error instanceof PathTooLongFail: {
                descriptor.errorFile = { status: EFileStatus.ERROR, error: EFileError.NAME_TOO_LONG };
                break;
            }
            case error instanceof InvalidCharactesFail: {
                descriptor.errorFile = { status: EFileStatus.ERROR, error: EFileError.INVALID_CHARACTERS };
                break;
            }
        }

        if (!descriptor.errorFile) {
            if (hasInvalidCharAutoFix) {
                descriptor.errorFile = { error: EFileError.INVALID_CHARACTERS_AUTO_FIX };
            } else if (hasNameTooLongAutoFix) {
                descriptor.errorFile = { error: EFileError.NAME_TOO_LONG_AUTO_FIX };
            }
        }

        if (
            !descriptorOptions.continueUpload &&
            !descriptor.uploadingPacketConfig.isPublicUpload &&
            entries.length < MAX_FILES_TO_STORE_IN_INDEX_DB &&
            !descriptor.isDirectory
        ) {
            const file = await descriptor.getFile();
            if (file) {
                entries.push([initialCloudPath, file]);
            }
        }

        fileDescriptors.push({
            cloudPath,
            size,
            name: nameParts?.name || '',
            extension,
            descriptorId: id,
            localPath,
            hasAutoFix: {
                hasInvalidCharAutoFix,
                hasNameTooLongAutoFix,
            },
            status: EFileStatus.PROGRESS,
            ...(descriptor.errorFile ? descriptor.errorFile : {}),
            ...descriptorOptions,
            thumb: '',
            uploadPacket: descriptor.uploadingPacketConfig.currentPacketIdForUI,
        });

        if (descriptor.errorFile) {
            sendGaUploaderNew('file', descriptor.errorFile.error);
            sendGaUploaderNew('testerror', getFileExtLimit(extension));
        }
    }

    if (entries.length) {
        setTimeout(async () => {
            // TODO: писать в стор паралельно пачками
            try {
                await setMany(entries);
            } catch (error) {
                sendXray(['uploader', 'idb-keyval', 'setindex', 'error']);
            }
        }, 100);
    }

    return fileDescriptors;
}

function autoFixError({
    error,
    cloudName,
    cloudPath,
    localPath,
}: {
    error: UploadingReason | null;
    cloudName: string;
    cloudPath: string;
    localPath: string;
}) {
    const maxNameLength = 50;
    let fixCloudName = cloudName;
    let fixCloudPath = cloudPath;
    let fixLocalPath = localPath;
    let fixError = error;
    let hasInvalidCharAutoFix = false;
    let hasNameTooLongAutoFix = false;

    if (error instanceof InvalidCharactesFail) {
        fixCloudName = removeInvalidNameCharacters(cloudName);
        fixCloudPath = removeInvalidPathCharacters(cloudPath);
        fixLocalPath = removeInvalidPathCharacters(localPath);
        fixError = null;
        hasInvalidCharAutoFix = true;

        sendGaUploaderNew('auto_fix', 'invalid_char');
    } else if (error instanceof NameTooLongFail) {
        fixCloudName = `${fixCloudName.slice(0, maxNameLength)}...${fixCloudName.slice(-maxNameLength)}`;
        fixCloudPath = `${fixCloudPath.slice(0, maxNameLength)}...${fixCloudName.slice(-maxNameLength)}`;
        fixLocalPath = `${fixLocalPath.slice(0, maxNameLength)}...${fixLocalPath.slice(-maxNameLength)}`;
        fixError = null;
        hasNameTooLongAutoFix = true;
        sendGaUploaderNew('auto_fix', 'name_too_long');
    }

    return { fixCloudName, fixCloudPath, fixLocalPath, fixError, hasInvalidCharAutoFix, hasNameTooLongAutoFix };
}

export const getThumbnail = (descriptor: UploadingDescriptor) => {
    const extension = descriptor.nameParts?.extension;

    if (!extension) {
        return '';
    }

    const extensionData = extInfo.get(extension);
    const kind = extensionData.kind;
    const hash = descriptor.cloudHash;
    const size = descriptor.size;

    const id = `/fake.${extension}`;
    const isPublic = descriptor.uploadingPacketConfig.isPublicUpload;
    const thumbnails = urlBuilder.getThumb({ ext: extension.toLowerCase(), ext2: extension, kind, hash, size, isPublic, id });

    return thumbnails?.pic ?? '';
};

export const isWasmSupported = (() => {
    try {
        if (typeof WebAssembly === 'object' && typeof WebAssembly.instantiate === 'function') {
            const module = new WebAssembly.Module(Uint8Array.of(0x0, 0x61, 0x73, 0x6d, 0x01, 0x00, 0x00, 0x00));
            if (module instanceof WebAssembly.Module) {
                return new WebAssembly.Instance(module) instanceof WebAssembly.Instance;
            }
        }
    } catch (error) {}
    return false;
})();

export const getConnectionTypeString = () => {
    try {
        if ('connection' in navigator) {
            // @ts-ignore
            const effType = 'effectiveType' in navigator.connection && navigator.connection.effectiveType;
            const type = 'type' in navigator.connection && navigator.connection.type;
            return `connection:${type},${effType};`;
        }
    } catch (_) {
    }
    return '';
};
